'use client';

import type React from 'react';

import {
	useRef,
	useState,
	useCallback,
	useImperativeHandle,
	forwardRef,
	type Ref,
	useEffect,
	type ComponentType,
} from 'react';
import DataTable, {
	type SortOrder,
	type TableColumn,
	type ExpanderComponentProps,
} from 'react-data-table-component';
import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	AccordionItem,
	Badge,
	Button,
	Card,
	CardBody,
	CardText,
	CardTitle,
	Col,
	Container,
	Input,
	Row,
	Spinner,
	Table,
	Tooltip,
	Dropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem,
	Label,
	Modal,
} from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

import { apiAdapter } from '../../../config/adapters/api.adapter';
import { API_URLS } from '../../../constants/api_links';
import { AdvancedFilter, FilterConfig } from './AdvancedFilter';
import { BottomSheet } from '../modal/BottomSheet';

interface ActionsConfig {
	importUrl: string;
	exportUrl: string;
	templateUrl: string;
	onImportClick?: () => void;
}

interface ExpandableConfig<T> {
	component: ComponentType<ExpanderComponentProps<T>>;
	expandableRowDisabled?: (row: T) => boolean;
	expandOnRowClicked?: boolean;
	expandOnRowDoubleClicked?: boolean;
	expandableRowsHideExpander?: boolean;
}

interface Props<T, R> {
	route: string;
	columns: TableColumn<T>[];
	mapper?: (response: R) => { data: T[]; total: number };
	showSearch?: boolean;
	showButtons?: boolean;
	actionsConfig?: ActionsConfig;
	debounceTime?: number;
	expandableConfig?: ExpandableConfig<T>;
	extraParams?: Record<string, string | number | boolean | undefined | null>;
	customStyles?: any;
	enableColumnToggle?: boolean;
	enableCompactMode?: boolean;
	enableAdvancedFilter?: boolean;
	onDataLoad?: (data: { data: T[]; total: number }) => void;
	extraActions?: React.ReactNode;
	hideMobileDefaultDetails?: boolean;
}

export interface CustomTableRef {
	refetch: () => void;
}

interface TooltipState {
	import: boolean;
	export: boolean;
	template: boolean;
}

const INITIAL_PAGE = 1;
const INITIAL_PER_PAGE = 10;
const INITIAL_SORT_FIELD = 'id';
const INITIAL_SORT_DIRECTION: SortOrder = 'desc' as SortOrder;

const useDebounce = (value: string, delay: number) => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
};

const CustomTableInner = <T, R>(
	{
		route,
		columns,
		mapper,
		showSearch = false,
		showButtons = false,
		actionsConfig,
		debounceTime = 400,
		expandableConfig,
		extraParams,
		customStyles,
		enableColumnToggle = false,
		enableCompactMode = false,
		enableAdvancedFilter = false,
		onDataLoad,
		extraActions,
		hideMobileDefaultDetails = false,
	}: Props<T, R>,
	ref: Ref<CustomTableRef>
) => {
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(INITIAL_PAGE);
	const [perPage, setPerPage] = useState(INITIAL_PER_PAGE);
	const [sortField, setSortField] = useState(INITIAL_SORT_FIELD);
	const [sortDirection, setSortDirection] = useState(INITIAL_SORT_DIRECTION);
	const [tooltipOpen, setTooltipOpen] = useState<TooltipState>({
		import: false,
		export: false,
		template: false,
	});
	const [isImporting, setIsImporting] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const columnsStorageKey = `custom_table_columns_${route}`;
	const filtersStorageKey = `custom_table_filters_${route}`;

	const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
		try {
			const saved = localStorage.getItem(columnsStorageKey);
			if (saved) {
				const parsed = JSON.parse(saved);
				if (Array.isArray(parsed)) return parsed;
			}
		} catch (e) {
			console.error('Error reading columns from localStorage', e);
		}
		return columns
			.map((c) => (typeof c.name === 'string' ? c.name : ''))
			.filter((name) => name !== '');
	});
	const [isCompact, setIsCompact] = useState(false);
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [activeFilters, setActiveFilters] = useState<FilterConfig[]>(() => {
		try {
			const saved = localStorage.getItem(filtersStorageKey);
			if (saved) {
				const parsed = JSON.parse(saved);
				if (Array.isArray(parsed)) return parsed;
			}
		} catch (e) {
			console.error('Error reading filters from localStorage', e);
		}
		return [];
	});
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const [detailRow, setDetailRow] = useState<any>(null);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768);
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		localStorage.setItem(columnsStorageKey, JSON.stringify(visibleColumns));
	}, [visibleColumns, columnsStorageKey]);

	useEffect(() => {
		localStorage.setItem(filtersStorageKey, JSON.stringify(activeFilters));
	}, [activeFilters, filtersStorageKey]);

	const debouncedSearch = useDebounce(search, debounceTime);

	const toggleTooltip = useCallback((key: keyof TooltipState) => {
		setTooltipOpen((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

	const fetchData = async () => {
		const finalSearch = extraParams?.search !== undefined ? extraParams.search : debouncedSearch;
		let url = `${route}?page=${page}&per_page=${perPage}&sort=${sortField}&sort_direction=${sortDirection}&search=${encodeURIComponent(String(finalSearch ?? ''))}`;
		
		if (activeFilters && activeFilters.length > 0) {
			const filterParams = activeFilters
				.map((f, i) => {
					const prefix = `filters[${i}]`;
					return `${prefix}[field]=${encodeURIComponent(f.field)}&${prefix}[operator]=${encodeURIComponent(f.operator)}&${prefix}[value]=${encodeURIComponent(String(f.value))}${f.value2 ? `&${prefix}[value2]=${encodeURIComponent(String(f.value2))}` : ''}`;
				})
				.join('&');
			url += `&${filterParams}`;
		}

		if (extraParams) {
			const queryParams = Object.entries(extraParams)
				.filter(([key, value]) => key !== 'search' && value !== undefined && value !== null && value !== '')
				.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
				.join('&');
			if (queryParams) {
				url += `&${queryParams}`;
			}
		}
		const response = await apiAdapter.get<R>(url);
		if (mapper) return mapper(response);

		const resp = response as any;
		// 1. Standard Laravel nested wrapper: { data: { data: [...], total: N } }
		if (resp?.data && Array.isArray(resp.data.data)) {
			return {
				data: resp.data.data,
				total: resp.data.total ?? resp.data.data.length,
			};
		}
		// 2. Flat paginated response: { data: [...], total: N }
		if (Array.isArray(resp?.data)) {
			return {
				data: resp.data,
				total: resp.total ?? resp.data.length,
			};
		}
		// 3. Direct array response: [...]
		if (Array.isArray(resp)) {
			return {
				data: resp,
				total: resp.length,
			};
		}
		// 4. Default fallback
		return {
			data: [],
			total: 0,
		};
	};

	const { data, isLoading, refetch } = useQuery({
		queryKey: [
			'data',
			route,
			page,
			perPage,
			sortField,
			sortDirection,
			extraParams?.search !== undefined ? extraParams.search : debouncedSearch,
			extraParams,
			activeFilters,
		],
		queryFn: fetchData,
	});

	const onDataLoadRef = useRef(onDataLoad);
	useEffect(() => {
		onDataLoadRef.current = onDataLoad;
	}, [onDataLoad]);

	useEffect(() => {
		if (data && onDataLoadRef.current) {
			onDataLoadRef.current(data);
		}
	}, [data]);

	useImperativeHandle(ref, () => ({
		refetch: () => {
			console.log('Refetch externo ejecutado');
			refetch({ cancelRefetch: false });
		},
	}));

	const handleImportClick = () => {
		if (actionsConfig?.onImportClick) {
			actionsConfig.onImportClick();
		} else {
			fileInputRef.current?.click();
		}
	};

	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const file = event.target.files?.[0];
		if (!file || !actionsConfig?.importUrl) return;

		const formData = new FormData();
		formData.append('file', file);

		try {
			setIsImporting(true);
			await apiAdapter.post(actionsConfig.importUrl, formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
			});
			toast.success('Archivo importado exitosamente');
			await refetch();
		} catch (error: any) {
			console.error('Error al importar:', error);
			const errorMsg = error?.message || error?.error || 'Error al importar el archivo';
			const backendErrors = error?.errors || error?.data?.errors || error?.data?.detail;

			if (backendErrors) {
				let formattedList = '';
				if (Array.isArray(backendErrors)) {
					formattedList = backendErrors
						.map((e) => (typeof e === 'string' ? e : e.message || JSON.stringify(e)))
						.map((msg) => `<li style="text-align: left; margin-bottom: 4px;">${msg}</li>`)
						.join('');
				} else if (typeof backendErrors === 'object') {
					formattedList = Object.entries(backendErrors)
						.map(([field, msgs]) => {
							const msgStr = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
							return `<li style="text-align: left; margin-bottom: 4px;"><strong>${field}:</strong> ${msgStr}</li>`;
						})
						.join('');
				}

				Swal.fire({
					icon: 'error',
					title: 'Error al importar archivo',
					html: `
						<div style="text-align: left; font-size: 0.9rem;">
							<p style="margin-bottom: 8px; font-weight: 600; color: #dc3545;">${errorMsg}</p>
							<div style="background: #f8f9fa; padding: 12px; border-radius: 8px; max-height: 250px; overflow-y: auto; border: 1px solid #e9ecef;">
								<ul style="padding-left: 18px; margin-bottom: 0;">${formattedList}</ul>
							</div>
						</div>
					`,
					confirmButtonText: 'Entendido',
				});
			} else {
				Swal.fire({
					icon: 'error',
					title: 'Error al importar',
					text: errorMsg,
				});
			}
		} finally {
			setIsImporting(false);
			if (fileInputRef.current) fileInputRef.current.value = '';
		}
	};

	const handleExport = async () => {
		try {
			setIsExporting(true);
			const response = await apiAdapter.get(API_URLS.EXPORT_INTEREST, {
				responseType: 'blob',
			});
			const blob = new Blob([response as Blob]);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'interests.xlsx');
			document.body.appendChild(link);
			link.click();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error al exportar el archivo:', error);
			toast.error('Error al exportar el archivo');
		} finally {
			setIsExporting(false);
		}
	};

	const handleDownloadTemplate = async () => {
		if (!actionsConfig?.templateUrl) {
			toast.error('No se encontró la URL de la plantilla');
			return;
		}
		try {
			const response = await apiAdapter.get(actionsConfig.templateUrl, {
				responseType: 'blob',
			});
			const blob = new Blob([response as Blob]);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'interest_template.xlsx');
			document.body.appendChild(link);
			link.click();
			window.URL.revokeObjectURL(url);
			toast.success('Plantilla descargada exitosamente');
		} catch (error) {
			console.error('Error al descargar la plantilla:', error);
			toast.error('Error al descargar la plantilla');
		}
	};

	const handleSort = (column: any, direction: SortOrder) => {
		console.log(column);
		setSortField(column.sortField || INITIAL_SORT_FIELD);
		setSortDirection(direction);
		setPage(INITIAL_PAGE);
	};

	const handleChangeRowsPerPage = (newPerPage: number, newPage: number) => {
		setPerPage(newPerPage);
		setPage(newPage);
	};

	const handleChangePage = (newPage: number) => {
		setPage(newPage);
	};

	const [expandedRows, setExpandedRows] = useState<Record<string | number, boolean>>({});

	const toggleRowExpand = (rowId: string | number) => {
		setExpandedRows((prev) => ({
			...prev,
			[rowId]: !prev[rowId],
		}));
	};

	const handleRowClick = (row: any) => {
		if (expandableConfig) {
			toggleRowExpand(row.id);
		}
	};

	const renderMobileCards = () => {
		if (!data?.data || data.data.length === 0) {
			return (
				<div className="text-center py-4 text-muted small">
					No hay registros disponibles.
				</div>
			);
		}

		return (
			<div className="mobile-list-group border rounded-3 bg-white overflow-hidden shadow-sm">
				{data.data.map((row: any, rowIndex: number) => {
					const isExpanded = expandedRows[row.id] || false;

					// Encontrar columnas específicas para el formato de listado móvil
					const colUser = columns.find(c => c.name === 'Concepto' || c.name === 'Categoría' || c.name === 'Usuario') || columns[1];
					const colTotal = columns.find(c => c.name === 'Pago Total' || c.name === 'Monto' || c.name === 'Valor de interés') || columns[columns.length - 3];
					const colDate = columns.find(c => c.name === 'Fecha de pago' || c.name === 'Fecha de operación' || c.name === 'Fecha de transacción' || c.name === 'Fecha') || columns[columns.length - 2];
					const colInstallments = columns.find(c => c.name === 'Cuotas');
					const colMonth = columns.find(c => c.name === 'Mes Contable');

					const titleVal = colUser?.selector ? colUser.selector(row, rowIndex) : '';
					const totalVal = colTotal?.selector ? colTotal.selector(row, rowIndex) : '';
					const dateVal = colDate?.selector ? colDate.selector(row, rowIndex) : '';
					const instVal = colInstallments?.selector ? colInstallments.selector(row, rowIndex) : '';
					const monthVal = colMonth?.selector ? colMonth.selector(row, rowIndex) : '';

					// Columna de acciones (ID vacío, tiene función cell)
					const actionsCol = columns.find(col => col.cell && (!col.name || col.name === '' || col.name === 'Acciones'));
					const renderActions = actionsCol?.cell;

					const isFamilyGroupRoute = route === '/family-groups' || route.includes('family-groups');
					const colProgress = columns.find(c => c.name === 'Uso de Límite');
					const progressCell = colProgress?.cell ? colProgress.cell(row, rowIndex, colProgress, '') : null;

					return (
						<div key={row.id || rowIndex} className="mobile-list-item border-bottom animate-fade-in">
							{/* Fila principal de datos del registro */}
							<div 
								className="d-flex justify-content-between align-items-center p-3 cursor-pointer hover-bg-light position-relative"
								onClick={() => handleRowClick(row)}
								style={{ transition: 'background-color 0.15s ease' }}
							>
								{/* Lado Izquierdo: Usuario e información secundaria (ID, Fecha, Cuotas) o Barra de Uso */}
								<div className="d-flex flex-column" style={{ minWidth: 0, flex: 1, paddingRight: '12px' }}>
									<span className="text-dark fw-bold text-truncate mb-1" style={{ fontSize: '0.925rem' }}>
										{titleVal || 'Registro'}
									</span>
									{isFamilyGroupRoute ? (
										<div className="mt-2 w-100">
											{progressCell}
										</div>
									) : (
										<span className="text-muted d-flex flex-wrap gap-1 align-items-center" style={{ fontSize: '0.72rem' }}>
											{dateVal ? (
												<span className="fw-semibold text-secondary">{dateVal}</span>
											) : (
												<span className="fw-semibold text-primary">ID #{row.id}</span>
											)}
											{monthVal && (
												<>
													<span className="text-black-50">•</span>
													<span className="fw-semibold text-primary">{monthVal}</span>
												</>
											)}
											{row.type && (
												<>
													<span className="text-black-50">•</span>
													<span 
														style={{ 
															fontSize: '0.625rem', 
															fontWeight: 700, 
															textTransform: 'uppercase', 
															padding: '2px 6px', 
															borderRadius: '12px',
															backgroundColor: row.type === 'expense' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)',
															color: row.type === 'expense' ? '#ef4444' : '#10b981',
															border: row.type === 'expense' ? '1px solid rgba(239, 68, 68, 0.15)' : '1px solid rgba(16, 185, 129, 0.15)',
															display: 'inline-block',
															lineHeight: '1'
														}}
													>
														{row.type === 'expense' ? 'Gasto' : 'Ingreso'}
													</span>
												</>
											)}
											{instVal && (
												<>
													<span className="text-black-50">•</span>
													<span className="badge bg-secondary-subtle text-secondary py-0.5 px-1.5" style={{ fontSize: '0.65rem' }}>
														{instVal} {Number(instVal) === 1 ? 'Cuota' : 'Cuotas'}
													</span>
												</>
											)}
										</span>
									)}
								</div>

								{/* Lado Derecho: Monto y botones de acción */}
								<div className="d-flex align-items-center gap-2.5 text-end">
									<div className="d-flex flex-column align-items-end">
										{totalVal ? (
											<span 
												className={row.type === 'expense' ? 'text-danger fw-bold mb-1' : 'text-success fw-bold mb-1'} 
												style={{ fontSize: '0.95rem' }}
											>
												{totalVal}
											</span>
										) : null}
										<div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
											{renderActions && (
												<div className="mobile-item-actions mt-0">
													{renderActions(row, rowIndex, actionsCol, 'actions')}
												</div>
											)}
											{!expandableConfig && !hideMobileDefaultDetails && (
												<button
													type="button"
													className="btn btn-sm btn-icon btn-outline-primary d-flex align-items-center justify-content-center"
													style={{
														width: '32px',
														height: '32px',
														borderRadius: '50%',
														padding: '0',
														border: '1px solid rgba(7, 102, 173, 0.15)',
														backgroundColor: 'rgba(7, 102, 173, 0.05)',
														color: '#0766ad',
														boxShadow: 'none'
													}}
													onClick={(e) => {
														e.stopPropagation();
														setDetailRow(row);
													}}
												>
													<i className="fas fa-eye" style={{ fontSize: '0.8rem' }}></i>
												</button>
											)}
										</div>
									</div>
									
									{expandableConfig && (
										<i className={`fa fa-chevron-right text-muted ${isExpanded ? 'rotate-90' : ''}`} style={{ fontSize: '0.75rem', transition: 'transform 0.2s ease', marginLeft: '6px' }}></i>
									)}
								</div>
							</div>

							{/* Fila expandida con todos los detalles */}
							{expandableConfig && isExpanded && (
								<div className="p-3 bg-light border-top animate-fade-in">
									<expandableConfig.component data={row} />
								</div>
							)}
						</div>
					);
				})}
			</div>
		);
	};

	const activeColumns = columns.filter(
		(c) =>
			!c.name ||
			typeof c.name !== 'string' ||
			visibleColumns.includes(c.name)
	);

	return (
		<div className="w-100 px-0 custom-table-container">
			{(showSearch || showButtons || enableColumnToggle || enableCompactMode || enableAdvancedFilter) && (
				<Row className="mb-3 align-items-center g-2">
					<Col md={showSearch ? 3 : 12} className="d-flex gap-2 align-items-center">
						{showSearch && (
							<Input
								type="text"
								placeholder="Buscar..."
								className="form-control"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								style={{ 
									flex: isMobile ? 1 : undefined,
									maxWidth: isMobile ? 'none' : '300px',
									borderRadius: isMobile ? '10px' : undefined,
									height: isMobile ? '40px' : undefined,
									backgroundColor: isMobile ? '#f8fafc' : undefined,
									border: isMobile ? '1px solid #e2e8f0' : undefined
								}}
							/>
						)}
						{isMobile && enableAdvancedFilter && (
							<AdvancedFilter
								columns={columns}
								isOpen={isFilterOpen}
								onToggle={() => setIsFilterOpen(!isFilterOpen)}
								onApplyFilters={(filters) => {
									setActiveFilters(filters);
									setPage(INITIAL_PAGE);
								}}
								onClearFilters={() => {
									setActiveFilters([]);
									setPage(INITIAL_PAGE);
								}}
								activeFiltersCount={activeFilters.length}
								initialFilters={activeFilters}
							/>
						)}
					</Col>
					<Col md={showSearch ? 9 : 12} className="text-end d-flex align-items-center justify-content-end gap-2 flex-wrap">
						{extraActions}
						{extraActions && (enableCompactMode || enableColumnToggle || enableAdvancedFilter || showButtons) && (
							<span className="text-muted d-none d-md-inline" style={{ opacity: 0.35, paddingLeft: '4px', paddingRight: '4px' }}>|</span>
						)}
						{!isMobile && enableCompactMode && (
							<Button
								color="none"
								onClick={() => setIsCompact(!isCompact)}
								title="Vista compacta"
								className={`d-flex align-items-center btn-sm fw-medium ${isCompact ? 'text-white' : 'text-secondary'}`}
								style={{
									backgroundColor: isCompact ? '#6c757d' : 'rgba(108, 117, 125, 0.06)',
									border: isCompact ? '1px solid #6c757d' : '1px solid rgba(108, 117, 125, 0.15)',
									borderRadius: '8px',
									height: '34px',
									padding: '0 12px',
									fontSize: '0.8rem',
									boxShadow: '0 2px 4px rgba(108, 117, 125, 0.04)',
									transition: 'all 0.2s ease',
									fontWeight: 600,
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center'
								}}
								onMouseEnter={(e) => {
									if (isCompact) {
										e.currentTarget.style.backgroundColor = '#5a6268';
									} else {
										e.currentTarget.style.backgroundColor = 'rgba(108, 117, 125, 0.12)';
									}
									e.currentTarget.style.transform = 'translateY(-1px)';
									e.currentTarget.style.boxShadow = '0 4px 8px rgba(108, 117, 125, 0.08)';
								}}
								onMouseLeave={(e) => {
									if (isCompact) {
										e.currentTarget.style.backgroundColor = '#6c757d';
									} else {
										e.currentTarget.style.backgroundColor = 'rgba(108, 117, 125, 0.06)';
									}
									e.currentTarget.style.transform = 'none';
									e.currentTarget.style.boxShadow = '0 2px 4px rgba(108, 117, 125, 0.04)';
								}}
							>
								<i className={`fa ${isCompact ? 'fa-align-justify' : 'fa-list'} me-2 ${isCompact ? 'text-white' : 'text-secondary'}`} style={{ fontSize: '0.85rem' }}></i>
								Vista compacta
							</Button>
						)}

						{!isMobile && enableColumnToggle && (
							<Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)} size="sm">
								<DropdownToggle 
									color="none" 
									caret={false}
									className="d-flex align-items-center btn-sm text-primary fw-medium"
									style={{
										backgroundColor: 'rgba(7, 102, 173, 0.06)',
										border: '1px solid rgba(7, 102, 173, 0.15)',
										borderRadius: '8px',
										height: '34px',
										padding: '0 12px',
										fontSize: '0.8rem',
										boxShadow: '0 2px 4px rgba(7, 102, 173, 0.04)',
										transition: 'all 0.2s ease',
										fontWeight: 600,
										display: 'inline-flex',
										alignItems: 'center',
										justifyContent: 'center'
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.backgroundColor = 'rgba(7, 102, 173, 0.12)';
										e.currentTarget.style.transform = 'translateY(-1px)';
										e.currentTarget.style.boxShadow = '0 4px 8px rgba(7, 102, 173, 0.08)';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.backgroundColor = 'rgba(7, 102, 173, 0.06)';
										e.currentTarget.style.transform = 'none';
										e.currentTarget.style.boxShadow = '0 2px 4px rgba(7, 102, 173, 0.04)';
									}}
								>
									<i className="fa fa-columns me-2 text-primary" style={{ fontSize: '0.85rem' }}></i>
									Columnas
									<i className="fa fa-chevron-down ms-2 text-primary" style={{ fontSize: '0.65rem', opacity: 0.8 }}></i>
								</DropdownToggle>
								<DropdownMenu 
									onClick={(e) => e.stopPropagation()} 
									style={{ 
										padding: '12px', 
										minWidth: '240px', 
										maxHeight: '320px', 
										overflowY: 'auto',
										borderRadius: '12px',
										boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
										border: '1px solid rgba(0,0,0,0.06)'
									}}
								>
									<DropdownItem header className="px-2 border-bottom pb-2 mb-2 bg-transparent text-primary fw-bold">
										Mostrar/Ocultar Columnas
									</DropdownItem>
									{columns.map((c, i) => {
										const colName = c.name;
										if (typeof colName !== 'string' || !colName) return null;
										const isChecked = visibleColumns.includes(colName);
										return (
											<div key={i} className="dropdown-item py-1.5 px-2 bg-transparent border-0">
												<div className="form-check form-switch mb-0">
													<input
														type="checkbox"
														role="switch"
														className="form-check-input cursor-pointer"
														id={`col-toggle-${i}`}
														checked={isChecked}
														onChange={() => {
															if (isChecked) {
																setVisibleColumns(visibleColumns.filter(name => name !== colName));
															} else {
																setVisibleColumns([...visibleColumns, colName]);
															}
														}}
													/>
													<label
														className="form-check-label ms-2 mb-0 cursor-pointer text-dark small"
														htmlFor={`col-toggle-${i}`}
														style={{ userSelect: 'none', fontWeight: 500 }}
													>
														{colName}
													</label>
												</div>
											</div>
										);
									})}
								</DropdownMenu>
							</Dropdown>
						)}

						{!isMobile && enableAdvancedFilter && (
							<AdvancedFilter
								columns={columns}
								isOpen={isFilterOpen}
								onToggle={() => setIsFilterOpen(!isFilterOpen)}
								onApplyFilters={(filters) => {
									setActiveFilters(filters);
									setPage(INITIAL_PAGE);
								}}
								onClearFilters={() => {
									setActiveFilters([]);
									setPage(INITIAL_PAGE);
								}}
								activeFiltersCount={activeFilters.length}
								initialFilters={activeFilters}
							/>
						)}

						{showButtons && actionsConfig && (
							<>
								<input
									type="file"
									ref={fileInputRef}
									style={{ display: 'none' }}
									onChange={handleFileChange}
									accept=".csv,.xlsx"
								/>
								<Button
									id="import-btn"
									color="primary"
									className="btn-sm"
									onClick={handleImportClick}
									disabled={isImporting}
								>
									{isImporting ? (
										<Spinner size="sm" />
									) : (
										<i className="fa fa-upload"></i>
									)}
								</Button>
								<Tooltip
									isOpen={tooltipOpen.import}
									target="import-btn"
									toggle={() => toggleTooltip('import')}
								>
									Importar
								</Tooltip>

								<Button
									id="export-btn"
									color="success"
									className="btn-sm"
									onClick={handleExport}
									disabled={isExporting}
								>
									{isExporting ? (
										<Spinner size="sm" />
									) : (
										<i className="fa fa-download"></i>
									)}
								</Button>
								<Tooltip
									isOpen={tooltipOpen.export}
									target="export-btn"
									toggle={() => toggleTooltip('export')}
								>
									Exportar
								</Tooltip>

								<Button
									id="template-btn"
									color="info"
									className="btn-sm"
									onClick={handleDownloadTemplate}
								>
									<i className="fa fa-file-excel"></i>
								</Button>
								<Tooltip
									isOpen={tooltipOpen.template}
									target="template-btn"
									toggle={() => toggleTooltip('template')}
								>
									Descargar Plantilla
								</Tooltip>
							</>
						)}
					</Col>
				</Row>
			)}

			{/* Vista Desktop (Tabla) */}
			<Row className="d-none d-md-block">
				<Col>
					<DataTable
						data={data?.data || []}
						columns={activeColumns}
						pagination
						paginationServer
						sortServer
						highlightOnHover
						striped
						dense={isCompact}
						paginationTotalRows={data?.total || 0}
						progressPending={isLoading}
						onSort={handleSort}
						onChangeRowsPerPage={handleChangeRowsPerPage}
						onChangePage={handleChangePage}
						className="table-responsive"
						customStyles={customStyles || {
							table: {
								style: {
									minHeight: '400px',
								},
							},
						}}
						expandableRows={!!expandableConfig}
						expandableRowsComponent={expandableConfig?.component}
						expandableRowDisabled={expandableConfig?.expandableRowDisabled}
						expandOnRowClicked={expandableConfig?.expandOnRowClicked}
						expandOnRowDoubleClicked={
							expandableConfig?.expandOnRowDoubleClicked
						}
						expandableRowsHideExpander={
							expandableConfig?.expandableRowsHideExpander
						}
					/>
				</Col>
			</Row>

			{/* Vista Móvil (Cards) */}
			<Row className="d-block d-md-none mx-0">
				<Col className="px-0">
					{isLoading ? (
						<div className="text-center py-5">
							<Spinner color="primary" />
							<p className="text-muted small mt-2">Cargando registros...</p>
						</div>
					) : (
						<>
							{renderMobileCards()}
							{/* Paginación Móvil */}
							{data?.total && data.total > perPage ? (
								<div className="d-flex justify-content-between align-items-center mt-3 px-2 py-2 bg-white rounded-3 shadow-sm border border-light-subtle" style={{ fontSize: '0.8rem' }}>
									<Button
										color="primary"
										outline
										size="sm"
										disabled={page === 1}
										onClick={() => handleChangePage(page - 1)}
										className="px-3"
									>
										Anterior
									</Button>
									<span className="text-muted fw-semibold">Pág. {page} de {Math.ceil(data.total / perPage)}</span>
									<Button
										color="primary"
										outline
										size="sm"
										disabled={page >= Math.ceil(data.total / perPage)}
										onClick={() => handleChangePage(page + 1)}
										className="px-3"
									>
										Siguiente
									</Button>
								</div>
							) : null}
						</>
					)}
				</Col>
			</Row>

			{/* Modal de detalles tipo Bottom Sheet en Móvil */}
			{isMobile && (
				<BottomSheet
					isOpen={!!detailRow}
					onClose={() => setDetailRow(null)}
					title="Detalles del Registro"
				>
					<div className="row g-3">
						{detailRow && columns
							.filter(c => c.name && typeof c.name === 'string' && c.name !== 'Acciones' && c.name !== 'Actions')
							.map((col, colIdx) => {
								const val = col.cell 
									? col.cell(detailRow, colIdx, col, '') 
									: col.selector 
										? col.selector(detailRow, colIdx) 
										: '';
								return (
									<div key={colIdx} className="col-12 border-bottom pb-2 mb-1">
										<span 
											className="text-muted d-block fw-semibold" 
											style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
										>
											{col.name}
										</span>
										<span 
											className="text-dark d-block fw-bold mt-1" 
											style={{ fontSize: '0.9rem', wordBreak: 'break-word' }}
										>
											{val !== undefined && val !== null && val !== '' ? val : '—'}
										</span>
									</div>
								);
							})}
					</div>
				</BottomSheet>
			)}
		</div>
	);
};

// Export correcto con tipado genérico + ref
export const CustomTable = forwardRef(CustomTableInner) as <T, R>(
	props: Props<T, R> & { ref?: Ref<CustomTableRef> }
) => ReturnType<typeof CustomTableInner>;

const ExpandedComponent = ({ data }: { data: any }) => {
	return (
		<div style={{ padding: '15px', backgroundColor: '#f8f9fa' }}>
			<Accordion open="" toggle={() => {}}>
				{/* Información Principal de Financial Assistance */}
				<AccordionItem>
					<AccordionHeader targetId="1">
						<strong>Información de la Asistencia Financiera</strong>
					</AccordionHeader>
					<AccordionBody accordionId="1">
						<Row>
							<Col md={6}>
								<Card
									className="mb-3"
									style={{ borderLeft: '4px solid #007bff' }}
								>
									<CardBody>
										<CardTitle tag="h6" className="text-primary">
											Datos Principales
										</CardTitle>
										<CardText>
											<strong>Monto Solicitado:</strong> $
											{data.requested_amount?.toLocaleString()}
										</CardText>
										<CardText>
											<strong>Cuotas:</strong> {data.installments}
										</CardText>
										<CardText>
											<strong>Estado:</strong>
											<Badge
												color={
													data.status === 'approved'
														? 'success'
														: data.status === 'pending'
															? 'warning'
															: 'secondary'
												}
												className="ms-2"
											>
												{data.status}
											</Badge>
										</CardText>
									</CardBody>
								</Card>
							</Col>

							<Col md={6}>
								<Card
									className="mb-3"
									style={{ borderLeft: '4px solid #28a745' }}
								>
									<CardBody>
										<CardTitle tag="h6" className="text-success">
											Referencia Familiar
										</CardTitle>
										<CardText>
											<strong>Nombre:</strong>{' '}
											{data.name_family_reference || 'No especificado'}
										</CardText>
										<CardText>
											<strong>Teléfono:</strong>{' '}
											{data.phone_family_reference || 'No especificado'}
										</CardText>
									</CardBody>
								</Card>
							</Col>
						</Row>
					</AccordionBody>
				</AccordionItem>

				{/* Información del Usuario Externo */}
				{data.external_user && (
					<AccordionItem>
						<AccordionHeader targetId="2">
							<strong>Información del Usuario</strong>
						</AccordionHeader>
						<AccordionBody accordionId="2">
							<Row>
								<Col md={6}>
									<Card
										className="mb-3"
										style={{ borderLeft: '4px solid #ffc107' }}
									>
										<CardBody>
											<CardTitle tag="h6" className="text-warning">
												Datos Personales
											</CardTitle>
											{data.external_user.user && (
												<>
													<CardText>
														<strong>Nombre:</strong>{' '}
														{data.external_user.user.name}
													</CardText>
													<CardText>
														<strong>Email:</strong>{' '}
														{data.external_user.user.email}
													</CardText>
													<CardText>
														<strong>Teléfono:</strong>{' '}
														{data.external_user.user.phone || 'No especificado'}
													</CardText>
												</>
											)}
										</CardBody>
									</Card>
								</Col>

								<Col md={6}>
									<Card
										className="mb-3"
										style={{ borderLeft: '4px solid #17a2b8' }}
									>
										<CardBody>
											<CardTitle tag="h6" className="text-info">
												Información Bancaria
											</CardTitle>
											{data.external_user.bank && (
												<>
													<CardText>
														<strong>Banco:</strong>{' '}
														{data.external_user.bank.name}
													</CardText>
													<CardText>
														<strong>Cuenta:</strong>{' '}
														{data.external_user.bank.account_number ||
															'No especificado'}
													</CardText>
													<CardText>
														<strong>Tipo:</strong>{' '}
														{data.external_user.bank.account_type ||
															'No especificado'}
													</CardText>
												</>
											)}
										</CardBody>
									</Card>
								</Col>
							</Row>
						</AccordionBody>
					</AccordionItem>
				)}

				{/* Portafolio de Crédito */}
				{data.credit_portfolio && (
					<AccordionItem>
						<AccordionHeader targetId="3">
							<strong>Portafolio de Crédito</strong>
						</AccordionHeader>
						<AccordionBody accordionId="3">
							<Card className="mb-3">
								<CardBody>
									<Row>
										<Col md={4}>
											<CardText>
												<strong>Fecha de Operación:</strong>{' '}
												{new Date(
													data.credit_portfolio.operation_date
												).toLocaleDateString()}
											</CardText>
											<CardText>
												<strong>Deuda Inicial:</strong> $
												{data.credit_portfolio.initial_debt?.toLocaleString()}
											</CardText>
											<CardText>
												<strong>Obligación Financiera:</strong> $
												{data.credit_portfolio.financial_obligation?.toLocaleString()}
											</CardText>
										</Col>
										<Col md={4}>
											<CardText>
												<strong>Seguro:</strong> $
												{data.credit_portfolio.insurance?.toLocaleString()}
											</CardText>
											<CardText>
												<strong>Cuota:</strong> $
												{data.credit_portfolio.fee?.toLocaleString()}
											</CardText>
											<CardText>
												<strong>Tasa de Crédito:</strong>{' '}
												{data.credit_portfolio.credit_rate}%
											</CardText>
										</Col>
										<Col md={4}>
											<CardText>
												<strong>Periodos:</strong>{' '}
												{data.credit_portfolio.periods}
											</CardText>
											<CardText>
												<strong>Periodicidad:</strong>{' '}
												{data.credit_portfolio.periodicity}
											</CardText>
										</Col>
									</Row>
								</CardBody>
							</Card>

							{/* Amortizaciones */}
							{data.credit_portfolio.external_amortizations &&
								data.credit_portfolio.external_amortizations.length > 0 && (
									<Card>
										<CardBody>
											<CardTitle tag="h6" className="mb-3">
												Plan de Amortizaciones
											</CardTitle>
											<Table striped responsive>
												<thead>
													<tr>
														<th>Periodo</th>
														<th>Fecha Pago</th>
														<th>Saldo Inicial</th>
														<th>Pago</th>
														<th>Amortización</th>
														<th>Interés</th>
														<th>Seguro</th>
														<th>Cuota</th>
														<th>Saldo Final</th>
														<th>Estado Pago</th>
													</tr>
												</thead>
												<tbody>
													{data.credit_portfolio.external_amortizations.map(
														(amortization: any, index: number) => (
															<tr key={index}>
																<td>{amortization.payment_periods}</td>
																<td>
																	{new Date(
																		amortization.payment_date
																	).toLocaleDateString()}
																</td>
																<td>
																	$
																	{amortization.initial_balance?.toLocaleString()}
																</td>
																<td>
																	${amortization.payment?.toLocaleString()}
																</td>
																<td>
																	${amortization.amortization?.toLocaleString()}
																</td>
																<td>
																	${amortization.interest?.toLocaleString()}
																</td>
																<td>
																	${amortization.insurance?.toLocaleString()}
																</td>
																<td>${amortization.fee?.toLocaleString()}</td>
																<td>
																	$
																	{amortization.final_balance?.toLocaleString()}
																</td>
																<td>
																	<Badge
																		color={
																			amortization.payment
																				? 'success'
																				: 'warning'
																		}
																	>
																		{amortization.payment
																			? 'Pagado'
																			: 'Pendiente'}
																	</Badge>
																</td>
															</tr>
														)
													)}
												</tbody>
											</Table>
										</CardBody>
									</Card>
								)}
						</AccordionBody>
					</AccordionItem>
				)}
			</Accordion>
		</div>
	);
};

export default ExpandedComponent;
